import { AxiosError } from 'axios';
import axiosInstance from '../api/axiosInstance';
import { DayEndClosureStatus, DayEndClosure, DenominationData } from '../types/dayEndClosure';

// Preview interfaces
interface OpeningBalancePreview {
  expected_opening_balance: number;
  previous_day_closed: boolean;
  previous_closing_balance: number;
  can_open_day: boolean;
  message: string;
}

interface ClosingBalancePreview {
  opening_balance: number;
  expected_closing_balance: number;
  today_movement: number;
  current_status: string;
  message: string;
}

interface DiscrepancyPreview {
  physical_count: number;
  expected_balance: number;
  discrepancy: number;
  discrepancy_type: 'shortage' | 'overage' | 'balanced';
  requires_adjustment: boolean;
  denomination_breakdown: DenominationData;
  message: string;
}

interface PhysicalCount {
  denomination_data: DenominationData;
  notes?: string;
}

class DayEndClosureService {
  private baseUrl = '/day-end-closure';

  async getStatus(siteId: number, businessDate?: string): Promise<DayEndClosureStatus> {
    try {
      const response = await axiosInstance.get<DayEndClosureStatus>(`${this.baseUrl}/status`, {
        params: { 
          site_id: siteId,
          ...(businessDate && { business_date: businessDate })
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching day-end closure status:', error);
      throw error;
    }
  }

  async openDay(siteId: number, businessDate?: string): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.post<DayEndClosure>(`${this.baseUrl}/open`, {
        site_id: siteId,
        ...(businessDate && { business_date: businessDate })
      });
      return response.data;
    } catch (error) {
      console.error('Error opening day:', error);
      throw error;
    }
  }

  async recordOpeningCount(
    closureId: number, 
    denominationData: DenominationData, 
    notes?: string
  ): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.post<DayEndClosure>(
        `${this.baseUrl}/${closureId}/record-opening-count`,
        {
          denomination_data: denominationData,
          notes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error recording opening count:', error);
      throw error;
    }
  }

  async recordClosingCount(
    closureId: number, 
    denominationData: DenominationData, 
    notes?: string
  ): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.post<DayEndClosure>(
        `${this.baseUrl}/${closureId}/record-closing-count`,
        {
          denomination_data: denominationData,
          notes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error recording closing count:', error);
      throw error;
    }
  }

  async finalizeDay(
    closureId: number, 
    confirmDiscrepancy: boolean, 
    adjustmentNotes?: string
  ): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.post<DayEndClosure>(
        `${this.baseUrl}/${closureId}/finalize`,
        {
          confirm_discrepancy: confirmDiscrepancy,
          adjustment_notes: adjustmentNotes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error finalizing day:', error);
      throw error;
    }
  }

  async getClosureHistory(
    siteId: number,
    skip: number = 0,
    limit: number = 50
  ): Promise<DayEndClosure[]> {
    try {
      const response = await axiosInstance.get<DayEndClosure[]>(`${this.baseUrl}/`, {
        params: {
          site_id: siteId,
          skip,
          limit
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching closure history:', error);
      throw error;
    }
  }

  async getClosureById(closureId: number): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.get<DayEndClosure>(`${this.baseUrl}/${closureId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching closure details:', error);
      throw error;
    }
  }

  // Preview endpoints
  async previewOpeningBalance(siteId: number, businessDate?: string): Promise<OpeningBalancePreview> {
    try {
      const response = await axiosInstance.get<OpeningBalancePreview>(
        `${this.baseUrl}/preview/opening-balance`,
        { 
          params: { 
            site_id: siteId,
            ...(businessDate && { business_date: businessDate })
          } 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching opening balance preview:', error);
      throw error;
    }
  }

  async previewClosingBalance(siteId: number, businessDate?: string): Promise<ClosingBalancePreview> {
    try {
      const response = await axiosInstance.get<ClosingBalancePreview>(
        `${this.baseUrl}/preview/closing-balance`,
        { 
          params: { 
            site_id: siteId,
            ...(businessDate && { business_date: businessDate })
          } 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching closing balance preview:', error);
      throw error;
    }
  }

  async previewDiscrepancy(
    countType: 'opening' | 'closing',
    siteId: number,
    denominationData: DenominationData,
    notes?: string,
    businessDate?: string
  ): Promise<DiscrepancyPreview> {
    try {
      const response = await axiosInstance.post<DiscrepancyPreview>(
        `${this.baseUrl}/preview/discrepancy`,
        { denomination_data: denominationData, notes },
        { 
          params: { 
            count_type: countType, 
            site_id: siteId,
            ...(businessDate && { business_date: businessDate })
          } 
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error calculating discrepancy preview:', error);
      throw error;
    }
  }

  // Atomic endpoints
  async openDayWithCount(
    siteId: number,
    physicalCount: PhysicalCount,
    businessDate?: string
  ): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.post<DayEndClosure>(
        `${this.baseUrl}/atomic/open-with-count`,
        {
          site_id: siteId,
          physical_count: physicalCount,
          ...(businessDate && { business_date: businessDate })
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error opening day with count:', error);
      throw error;
    }
  }

  async closeDayWithCount(
    closureId: number,
    physicalCount: PhysicalCount,
    confirmDiscrepancy: boolean,
    adjustmentNotes?: string
  ): Promise<DayEndClosure> {
    try {
      const response = await axiosInstance.post<DayEndClosure>(
        `${this.baseUrl}/atomic/close-with-count`,
        {
          closure_id: closureId,
          physical_count: physicalCount,
          confirm_discrepancy: confirmDiscrepancy,
          adjustment_notes: adjustmentNotes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error closing day with count:', error);
      throw error;
    }
  }

  getErrorMessage(error: unknown): string {
    if (error instanceof AxiosError && error.response?.data?.detail) {
      return error.response.data.detail;
    }
    return 'An unexpected error occurred';
  }
}

const dayEndClosureService = new DayEndClosureService();
export default dayEndClosureService;

// Export types for use in components
export type { 
  OpeningBalancePreview, 
  ClosingBalancePreview, 
  DiscrepancyPreview, 
  PhysicalCount 
};